import { useCallback, useEffect, useState } from 'react'
import { logger } from 'helpers/logger'
import { isAbortError } from 'helpers/error'
import useDebounce from 'hooks/useDebounce'
import { useAbortableRequest } from 'hooks/useAbortableRequest'
import { usePutApiV1MarkdownMutation } from '@wellesley/openapi'

export const useMarkdownApiPreview = (markdown: string, enabled = true) => {
  const [html, setHtml] = useState<string>('')

  const [abortablePutMarkdown, { isLoading, isError }] = useAbortableRequest(
    usePutApiV1MarkdownMutation,
  )

  const parseAndSet = useCallback(
    async (text: string) => {
      if (!enabled) return

      const trimmed = text.trim()
      if (!trimmed) {
        setHtml('')
        return
      }

      try {
        const result = await abortablePutMarkdown({ body: trimmed }).unwrap()
        const decoded = safeDecode(result)
        setHtml(decoded)
      } catch (err) {
        if (isAbortError(err as Error)) return
        logger.error('Failed to get markdown html:', err)
        setHtml('')
      }
    },
    [abortablePutMarkdown, enabled],
  )

  const debouncedParseAndSet = useDebounce(parseAndSet, 500)

  useEffect(() => {
    if (!enabled) return

    setHtml('')

    void debouncedParseAndSet(markdown)
  }, [debouncedParseAndSet, enabled, markdown])

  return {
    htmlString: html,
    isLoading,
    isError,
  }
}

const safeDecode = (result: unknown): string => {
  if (typeof result !== 'string') return String(result)

  // Handles both:
  // 1) '<p>111</p>\n<p>222</p>\n'
  // 2) '"<p>111</p>\\n<p>222</p>\\n"'
  try {
    const parsed = JSON.parse(result) as unknown
    return typeof parsed === 'string' ? parsed : result
  } catch {
    return result
  }
}
